import {EditorConfig} from '../../../constants/editorConfig'
import {panViewport, deselectNodes, setZoom} from '../../../editorActions'

const InteractionMode = EditorConfig.INTERACTION_MODES
const scaleBy = EditorConfig.ZOOM.WHEEL_SCALE_BY

/**
 * Bind stage-level input.
 */
export function bindStageInput(stage, editor, interaction, {isTextEditing, runtime} = {}) {
    let lastPos = null

    function onDragStart(e) {
        if (isTextEditing?.()) return
        if (!interaction.is(InteractionMode.PAN)) return
        lastPos = stage.getPointerPosition()
    }

    function onDragMove(e) {
        if (!interaction.is(InteractionMode.PAN)) return
        const currentPos = stage.getPointerPosition()
        if (!currentPos || !lastPos) return
        const currentScale = editor.getState().viewport.scale
        const dx = (currentPos.x - lastPos.x) / currentScale
        const dy = (currentPos.y - lastPos.y) / currentScale
        lastPos = currentPos
        editor.dispatch(panViewport(-dx, -dy))
    }

    function onDragEnd(e) {
        lastPos = null
    }

    /** @param {import('konva').KonvaEventObject<PointerEvent>} e */
    function onPointerDown(e) {
        if (e.target !== stage) return

        if (isTextEditing?.()) {
            runtime?.commitTextEditing()
            return
        }

        editor.dispatch(deselectNodes(editor.getState().selection.nodeIds))
    }

    /**
     * Wheel zoom
     * Uses editor state viewport; dispatch SET_ZOOM so core updates viewport.
     * Bound to the same element Konva uses (content) so the event is guaranteed to fire.
     */
    function onWheelNative(evt) {
        evt.preventDefault()

        const {viewport} = editor.getState()
        const oldScale = viewport.scale

        const rectTarget = stage.container() || stage.getContent?.()
        if (!rectTarget) return
        const rect = rectTarget.getBoundingClientRect()
        const pointer = {
            x: evt.clientX - rect.left,
            y: evt.clientY - rect.top,
        }

        const mousePointTo = {
            x: (pointer.x - -viewport.x * oldScale) / oldScale,
            y: (pointer.y - -viewport.y * oldScale) / oldScale,
        }

        let direction = evt.deltaY > 0 ? 1 : -1
        if (evt.ctrlKey) {
            direction = -direction
        }

        const newScale = direction > 0 ? oldScale * scaleBy : oldScale / scaleBy

        editor.dispatch(setZoom({scale: newScale, center: mousePointTo, pointer}))
    }

    stage.on('pointerdown', onPointerDown)
    stage.on('dragstart', onDragStart)
    stage.on('dragmove', onDragMove)
    stage.on('dragend', onDragEnd)

    // Bind wheel on the same element Konva uses (content), so we receive the event.
    // Defer so react-konva has set up the stage DOM; fallback to container if content not ready.
    let wheelTarget = null
    let rafId = null
    function attachWheel() {
        wheelTarget = stage.getContent?.() || stage.container()
        if (wheelTarget) {
            wheelTarget.addEventListener('wheel', onWheelNative, {passive: false})
        }
    }
    attachWheel()
    if (!wheelTarget) {
        rafId = requestAnimationFrame(() => {
            rafId = null
            wheelTarget = stage.getContent?.() || stage.container()
            if (wheelTarget) {
                wheelTarget.addEventListener('wheel', onWheelNative, {passive: false})
            }
        })
    }

    return function unbind() {
        if (rafId != null) cancelAnimationFrame(rafId)
        stage.off('pointerdown', onPointerDown)
        stage.off('dragstart', onDragStart)
        stage.off('dragmove', onDragMove)
        stage.off('dragend', onDragEnd)
        if (wheelTarget) {
            wheelTarget.removeEventListener('wheel', onWheelNative)
        }
    }
}
